import React from 'react';
import FiltersBar from '../../../Table/FiltersBar';
import Button from '../../../muiComponents/Button';
import useTranslate from '../../../customHooks/useTranslate';
import ChangeViewButtonBar from '../../../Table/ChangeViewButtonBar';
import Grid from '../../../muiComponents/Grid';
import GridChild from '../../../muiComponents/GridChild';
import { filterSingleColumnGrid } from '../../../utils/supplements/supplementUtils';
import LoadedOptionsTypeahead from '../../../muiComponents/LoadedOptionsTypeahead';
import {
    IFilterOptionState,
    ListViewType,
} from '../../../types/lists/listTypes';
import DateInput from '../../../muiComponents/DateInput';
import { useODataMultipleQueries } from '../../../customHooks/useODataMultipleQueries';
import { readOnlyEntities } from '../../../utils/commonVariables/entityKeys';
import { commonIcons } from '../../../utils/commonVariables/commonIcons';
import { useSearchParams } from '../../../customHooks/useSearchParams';
import {
    useStopSalesComplementaryData,
    useStopSalesList,
} from '../../contextsProperty/ContextStopSales';
import {
    gridSpacing10,
    gridSpacing15,
} from '../../../utils/commonVariables/grid';
import useUserProfile from '../../../customHooks/useUserProfile';
import Tooltip from '../../../muiComponents/Tooltip';

const StopSalesFiltersBar = () => {
    const {
        handleFilterChange,
        filters,
        handleSearch,
        resetFilters,
        isLoading,
        currentResults,
        totalResults,
        visibleView,
        changeVisibleView,
    } = useStopSalesList();
    const { readOnlyUser } = useUserProfile();

    const { supplierHotels } = useStopSalesComplementaryData();
    const supplierHotelSelected = filters.IdSupplierHotel
        .value as IFilterOptionState;
    const IdSupplierHotel = supplierHotelSelected?.Id
        ? parseInt(supplierHotelSelected?.Id)
        : null;

    const supplierHotelDependentQueries = [
        {
            key: 'supplierHotelRoomTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRoomType,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelMealPlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelMealPlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelRatePlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRatePlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
    ];

    const {
        supplierHotelRoomTypes,
        supplierHotelMealPlans,
        supplierHotelRatePlans,
    } = useODataMultipleQueries({
        queries: supplierHotelDependentQueries,
        dependencies: [IdSupplierHotel],
        condition: !!supplierHotelSelected,
    });

    const translate = useTranslate();

    const { setSearchParam } = useSearchParams({ base64: true });

    const handleAdd = () => {
        setSearchParam('id', 'new');
    };

    const buttonsButtonBar = [
        {
            icon:
                visibleView === ListViewType.CALENDAR
                    ? commonIcons.LIST
                    : commonIcons.CALENDAR,
            label: visibleView === ListViewType.CALENDAR ? 'list' : 'calendar',
            view:
                visibleView === ListViewType.CALENDAR
                    ? ListViewType.TABLE
                    : ListViewType.CALENDAR,
        },
    ];

    return (
        <FiltersBar
            title='stop_sales'
            clearFilters={resetFilters}
            search={async () => await handleSearch(1)}
            isLoading={isLoading}
            currentResults={currentResults}
            totalResults={totalResults}
            startButtonBar={
                <Tooltip
                    titleText='read_only_stop_sales'
                    hideTooltip={!readOnlyUser}
                >
                    <span>
                        <Button
                            variant='contained'
                            startIcon='Add'
                            onClick={handleAdd}
                            disabled={readOnlyUser}
                        >
                            {translate('add')}
                        </Button>{' '}
                    </span>
                </Tooltip>
            }
            endButtonBar={
                <ChangeViewButtonBar
                    changeVisibleView={(view: string) =>
                        changeVisibleView(view as ListViewType)
                    }
                    buttons={buttonsButtonBar}
                />
            }
        >
            <Grid
                className='flex-grow'
                columnSpacing={gridSpacing10}
                rowSpacing={gridSpacing15}
            >
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'hotel'}
                        options={supplierHotels.data || []}
                        value={filters.IdSupplierHotel.value || null}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) => handleFilterChange('IdSupplierHotel', newValue)}
                        disabled={supplierHotels.isLoading}
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'room'}
                        options={supplierHotelRoomTypes.data || []}
                        value={filters.IdSupplierHotelRoomType.value || null}
                        title={
                            !supplierHotelSelected
                                ? translate('choose_hotel_first')
                                : undefined
                        }
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) =>
                            handleFilterChange(
                                'IdSupplierHotelRoomType',
                                newValue
                            )
                        }
                        disabled={
                            !supplierHotelSelected ||
                            supplierHotelRoomTypes.isLoading
                        }
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'rate_plan'}
                        options={supplierHotelRatePlans.data || []}
                        value={filters.IdSupplierHotelRatePlan.value || null}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) =>
                            handleFilterChange(
                                'IdSupplierHotelRatePlan',
                                newValue
                            )
                        }
                        disabled={
                            !supplierHotelSelected ||
                            supplierHotelRoomTypes.isLoading
                        }
                        title={
                            !supplierHotelSelected
                                ? translate('choose_hotel_first')
                                : undefined
                        }
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'meal_plan'}
                        options={supplierHotelMealPlans.data || []}
                        value={filters.IdSupplierHotelMealPlan.value || null}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) =>
                            handleFilterChange(
                                'IdSupplierHotelMealPlan',
                                newValue
                            )
                        }
                        disabled={
                            !supplierHotelSelected ||
                            supplierHotelRoomTypes.isLoading
                        }
                        title={
                            !supplierHotelSelected
                                ? translate('choose_hotel_first')
                                : undefined
                        }
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <DateInput
                        value={filters.FromDate.value}
                        labelText={'from'}
                        onChange={(newDate: Date | null) => {
                            handleFilterChange('FromDate', newDate);
                        }}
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <DateInput
                        value={filters.ToDate.value}
                        labelText={'to'}
                        onChange={(newDate: Date | null) => {
                            handleFilterChange('ToDate', newDate);
                        }}
                    />
                </GridChild>
            </Grid>
        </FiltersBar>
    );
};

export default StopSalesFiltersBar;
